import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import type { AgentThreadSummary } from '@tabbio/types';
import { fetchThreads, deleteThread, updateThread } from '../lib/agent-threads';
import { QUERY_KEYS } from '../lib/query-keys';
import { useWorkspace } from '../providers/useWorkspace';

// This query backs the main menu's Recents list on BOTH surfaces (native
// drawer, web sidebar), and the menu renders every row it returns without
// virtualization. It is mounted for the whole session, so anything periodic
// here runs forever and gets more expensive the longer the account is used.
//
// It deliberately does NOT poll. A new thread reaches the list through
// `useInvalidateThreads` below, fired when the agent stream reports the
// thread it created - the one event that actually changes this list.
export function useThreads() {
  const { mode, companyId } = useWorkspace();

  const { data, isLoading, refetch } = useQuery({
    queryKey: QUERY_KEYS.THREADS(mode, companyId),
    queryFn: () => fetchThreads(mode, companyId),
    staleTime: 30_000,
    gcTime: 10 * 60_000,
    refetchOnMount: true,
    // Kept on (the app-wide default is off): on web the sidebar is always
    // visible with no open event, so returning to the tab is its freshness
    // trigger. Native maps this to app foreground via focusManager.
    refetchOnWindowFocus: true,
  });

  return { threads: data ?? [], isLoading, refetch };
}

/**
 * Marks the menu's Recents list stale for the active workspace. Call when a
 * thread is created or renamed server-side; replaces the interval poll that
 * used to be the only way a new chat appeared in the menu.
 */
export function useInvalidateThreads(scope?: { mode: 'seeker' | 'employer'; companyId?: string }) {
  const { mode: workspaceMode, companyId: workspaceCompanyId } = useWorkspace();
  const queryClient = useQueryClient();
  const mode = scope?.mode ?? workspaceMode;
  const companyId = scope ? scope.companyId : workspaceCompanyId;

  return useCallback(() => {
    void queryClient.invalidateQueries({
      queryKey: QUERY_KEYS.THREADS(mode, companyId),
    });
  }, [queryClient, mode, companyId]);
}

/**
 * Rename a thread, or pin it to the top of the menu.
 *
 * Optimistic, like the delete below: both act on a list the user is looking at,
 * and a rename that takes a network round-trip to appear reads as a failure.
 */
export function useUpdateThread() {
  const { mode, companyId } = useWorkspace();
  const queryClient = useQueryClient();
  const queryKey = QUERY_KEYS.THREADS(mode, companyId);

  return useMutation({
    mutationFn: ({ threadId, patch }: { threadId: string; patch: { title?: string; pinned?: boolean } }) => updateThread(threadId, patch, mode, companyId),
    onMutate: async ({ threadId, patch }) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<AgentThreadSummary[]>(queryKey);

      queryClient.setQueryData<AgentThreadSummary[]>(
        queryKey,
        (old) => old?.map((thread) => (thread.id === threadId ? { ...thread, ...patch } : thread)) ?? [],
      );

      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

export function useDeleteThread() {
  const { mode, companyId } = useWorkspace();
  const queryClient = useQueryClient();
  const queryKey = QUERY_KEYS.THREADS(mode, companyId);

  return useMutation({
    mutationFn: (threadId: string) => deleteThread(threadId, mode, companyId),
    onMutate: async (threadId) => {
      await queryClient.cancelQueries({ queryKey });

      const previous = queryClient.getQueryData<AgentThreadSummary[]>(queryKey);

      queryClient.setQueryData<AgentThreadSummary[]>(queryKey, (old) => old?.filter((t) => t.id !== threadId) ?? []);

      return { previous };
    },
    onError: (_err, _threadId, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}

/**
 * Bulk-delete all agent threads for the active workspace by looping the
 * existing per-thread DELETE endpoint. If this becomes a hot path, replace
 * with a single backend endpoint. Resolves with `{ deleted, failed }` counts.
 */
export function useClearAllThreads() {
  const { mode, companyId } = useWorkspace();
  const queryClient = useQueryClient();
  const queryKey = QUERY_KEYS.THREADS(mode, companyId);

  return useMutation({
    mutationFn: async (threads: AgentThreadSummary[]) => {
      let deleted = 0;
      let failed = 0;
      for (const thread of threads) {
        try {
          await deleteThread(thread.id, mode, companyId);
          deleted += 1;
        } catch {
          failed += 1;
        }
      }
      return { deleted, failed };
    },
    onMutate: async () => {
      await queryClient.cancelQueries({ queryKey });
      const previous = queryClient.getQueryData<AgentThreadSummary[]>(queryKey);
      queryClient.setQueryData<AgentThreadSummary[]>(queryKey, []);
      return { previous };
    },
    onError: (_err, _vars, context) => {
      if (context?.previous) {
        queryClient.setQueryData(queryKey, context.previous);
      }
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey });
    },
  });
}
